import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import { Grid } from 'views/anyguide/messages/styles';
import { Theme } from 'ui_system/theme';

import CheckmarkfilledIcn from 'views/ui/icons/checkmarkFilledIcon';
import Circle from 'views/ui/icons/circle';

const Radios = styled(Grid)`
  width: 100%;
  grid-gap: ${(props) =>
    props.format === 'wide'
      ? '10px'
      : props.format === 'minimal'
        ? '0'
        : '15px'};
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    grid-row-gap: 10px;
  }
`;

const Radio = styled(Grid)`
  color: ${Theme.colors.primaryTextColor};
  border: solid 1px ${Theme.colors.inputs.border};
  border-width: ${(props) => (props.borderLess ? '0' : '1px')};
  height: ${Theme.forms.dimensions.inputHeight}px;
  text-align: ${(props) => props.textAlign};
  align-items: center;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  font-size: ${(props) => (props.fontSize ? props.fontSize : '1.6rem')};
  opacity: ${(props) => (props.disabled ? '.3' : '1')};
  svg {
    height: 19px;
    top: 2px;
    position: relative;
  }
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    font-size: 15px;
    grid-template-columns: ${(props) => props.hasIcon && '32px 1fr 37px'};
    grid-gap: 1px;
    line-height: 16px;
    span {
      svg:first-child {
        height: 19px;
        left: 4px;
      }
    }
  }
`;

const RadioCircle = styled('div')`
  height: ${(props) =>
    props.circleSize
      ? props.circleSize
      : `${Theme.forms.dimensions.radioCircle}px`};
  width: ${(props) =>
    props.circleSize
      ? props.circleSize
      : `${Theme.forms.dimensions.radioCircle}px`};
  border-radius: 100px;
  border: ${(props) =>
    props.selected
      ? 'none'
      : `1px solid ${Theme.colors.borderActionableFieldColor}`};
  background: ${Theme.colors.backgroundColor};
  overflow: hidden;
  svg:first-child {
    display: ${(props) => (props.selected ? 'block' : 'none')};
    height: 100%;
    top: 0;
  }
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    height: ${(props) => (props.circleSize ? props.circleSize : '32px')};
    width: ${(props) => (props.circleSize ? props.circleSize : '32px')};
    svg:first-child {
      height: 30px;
    }
  }
`;

const CheckmarkIcon = styled(CheckmarkfilledIcn)`
  width: 100%;
  background-color: ${(props) =>
    props.color || Theme.colors.linksDesignElementsBaseColor};
  fill: #fff;
  height: 40px;
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    height: 32px;
    width: 32px;
  }
`;

const CircleFill = styled(Circle)`
  width: 100%;
`;

const RadioPair = (props) => {
  const {
    field,
    handleToggle,
    values,
    labels,
    icons,
    format,
    columnWidths,
    color,
    isCentered,
    isStacked,
    circleSize,
    fontSize,
    disabled,
  } = props;
  const toggle = (value, disabledItem) => {
    if (field.value === value || disabledItem) {
      return false;
    }
    handleToggle(field.name, value);
  };

  const renderRadio = (index) => {
    const disabledItem = disabled ? disabled[index] : false;
    let gridSpacing = icons ? '50px 1fr 40px' : '10px 1fr 35px';
    const textAlign = format === 'wide' || !icons ? 'left' : 'center';
    if (format === 'minimal') {
      gridSpacing = '0px 40px 1fr';
    }

    return (
      <Radio
        borderLess={format === 'minimal'}
        textAlign={textAlign}
        hasIcon={icons}
        gridTemplateColumns={gridSpacing}
        onClick={() => toggle(values[index], disabledItem)}
        fontSize={fontSize}
        disabled={disabledItem}
      >
        <span>{icons && icons[index]}</span>
        {format !== 'minimal' && labels[index]}
        <RadioCircle
          data-testid="edit-experience-radio-button"
          circleSize={circleSize}
          disabled={disabledItem}
          selected={
            JSON.stringify(field.value) === JSON.stringify(values[index])
          }
        >
          {props.simpleCircle ? (
            <CircleFill />
          ) : (
            <CheckmarkIcon color={color} />
          )}
        </RadioCircle>
        {format === 'minimal' && labels[index]}
      </Radio>
    );
  };

  let gridSpacing = format === 'wide' ? '1fr' : '1fr 1fr';
  if (format === 'minimal' && !isCentered) gridSpacing = '120px 1fr';
  if (format === 'minimal' && isStacked) gridSpacing = '1fr';
  // Prop overrides spacing
  if (columnWidths) gridSpacing = columnWidths;

  return (
    <Radios gridTemplateColumns={gridSpacing} format={format}>
      {renderRadio(0)}
      {renderRadio(1)}
    </Radios>
  );
};

RadioPair.defaultProps = {
  labels: ['Yes', 'No'],
  circleSize: null,
};

RadioPair.propTypes = {
  field: PropTypes.object.isRequired,
  handleToggle: PropTypes.func.isRequired,
  values: PropTypes.array,
  labels: PropTypes.array,
  icons: PropTypes.arrayOf(PropTypes.node),
  format: PropTypes.string,
  columnWidths: PropTypes.string,
  isCentered: PropTypes.bool,
  isStacked: PropTypes.bool,
  circleSize: PropTypes.string,
  fontSize: PropTypes.string,
  disabled: PropTypes.array,
};

export default RadioPair;
